// Client data functions — route to THIS app's BFF (same-origin, NEVER Core
// direct). Replaces the eb-ui `@eventbrite/event-renderer` `getEventsById`
// (destination upgrade) + `@eventbrite/legacy-infra.http` `sdkRequest`
// (pagination read) that `CollectionEvents` / `CardsCarousel` used.
//
//   (1) fetchCollectionEventsPage — the app's BFF pagination read at
//       `<basePath>/api/collections/{id}/events/` (relative fetch; the BFF
//       proxies the traced Core read).
//   (2) fetchDestinationEvents — the app's BFF destination read at
//       `<basePath>/api/events/?ids=...` (relative fetch; chunked by 50, the
//       BFF caps at 50), raw→FormattedEvent via the sanctioned
//       `transformDestinationEvent`.
// Deep-import the transform + types (the root barrel pulls the v3
// date-fns-tz `EventCardDate/hooks.js` — see `./eventRenderer`).
import {
    transformDestinationEvent,
    type DestinationEvent,
    type FormattedEvent,
} from './eventRenderer';

import type { EventListProps } from '@/types';

// BFF base = the app's basePath (`/cc`, fixed in next.config.js) + `/api`.
// MUST be basePath-prefixed ABSOLUTE, not relative: a relative `api/...` resolves
// under the current PAGE url (e.g. `/cc/<collection>/api/...`) → 404, not under
// the app's BFF (`/cc/api/...`). (Fleet pattern — sibling consumer-onboarding
// `BFF_BASE = '/u/interests/api'`; memory.md double-prepend trap.)
const BFF_BASE = '/cc/api';

const DESTINATION_EXPANSIONS = [
    'event_sales_status',
    'image',
    'primary_venue',
    'saves',
    'series',
    'ticket_availability',
    'primary_organizer',
];

function chunk<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
    }
    return out;
}

/**
 * (1) PAGINATION read — page >= 2 of a collection's events for a time filter.
 * Relative to the app's `/cc` basePath (the BFF at
 * `/cc/api/collections/{id}/events/`).
 *
 * BUGBOT (Medium): the past tab's SSR seed is page_size=18 (legacy Django
 * parity), while load-more previously kept requesting page_size=24. Core
 * offsets pages as (page-1)*page_size, so page 2@24 starts at event 25 and
 * past events 19–24 were unreachable. Pass the per-tab page size through so
 * past pagination is uniformly 18.
 */
export const PAST_EVENTS_PAGE_SIZE = 18;

export async function fetchCollectionEventsPage(
    id: string,
    timeFilter: 'current_future' | 'past',
    page: number,
    pageSize: number = 24,
): Promise<EventListProps> {
    const order = timeFilter === 'current_future' ? 'start_asc' : 'start_desc';
    const url = `${BFF_BASE}/collections/${id}/events/?time_filter=${timeFilter}&page=${page}&page_size=${pageSize}&order_by=${order}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
        throw new Error(`collection events page fetch failed: ${res.status}`);
    }
    return res.json() as Promise<EventListProps>;
}

/**
 * (2) DESTINATION read — upgrade a set of origin events to destination
 * `FormattedEvent` (adds primary_venue/price/sales_status/saves/
 * primary_organizer). Chunked by 50 (the BFF caps `ids` at 50), fetched from
 * the app's BFF at `/cc/api/events/?ids=...`, then mapped raw→FormattedEvent.
 */
export async function fetchDestinationEvents(
    idList: string[],
): Promise<{ events: FormattedEvent[] }> {
    if (idList.length === 0) {
        return { events: [] };
    }
    const seen = new Set<string>();
    const uniqueIds = idList.filter((id) => {
        if (seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    });
    const chunkIds = uniqueIds.slice(0, 50 * 4);
    const chunks = chunk(chunkIds, 50);
    const expand = DESTINATION_EXPANSIONS.join(',');

    const results: Promise<{ events: DestinationEvent[] }>[] = chunks.map(
        (c) =>
            fetch(
                `${BFF_BASE}/events/?ids=${encodeURIComponent(c.join(','))}&expand=${expand}`,
                { credentials: 'include' },
            )
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(
                            `destination events fetch failed: ${res.status}`,
                        );
                    }
                    return res.json() as Promise<{ events: DestinationEvent[] }>;
                }),
    );

    const responses = await Promise.all(results);
    const mergedRaw = responses.flatMap((r) => r.events ?? []);
    return {
        events: mergedRaw.map((event) => transformDestinationEvent(event)),
    };
}

/**
 * Legacy name, preserved so `CollectionEvents` / `CardsCarousel` call-sites
 * stay identical: the origin→destination upgrade read.
 *
 * NOTE: unlike the legacy `updateEventData` (which swallowed failures into
 * `{events:[]}`), this one KEEPS its rejection. `CollectionEvents` catches it
 * and falls back to the fetched ORIGIN events rather than silently dropping the
 * newly-loaded page; `CardsCarousel` catches it and keeps the seed. Confining
 * the throw makes the destination upgrade failure visible + recoverable.
 */
export async function updateEventData(
    idList: string[],
): Promise<{ events: FormattedEvent[] }> {
    return fetchDestinationEvents(idList);
}
